import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  RevisionConflictError,
  api,
  type Asset,
  type ExtractionStats,
  type Project,
  type Scenario,
} from '../api/client';
import { PlanCanvas } from '../editor/PlanCanvas';
import type { Selection } from '../editor/render';
import { TOOLS, toolByKey, type Tool } from '../editor/tools';
import {
  deleteOpening,
  deleteWall,
  newId,
  removeRoomMeta,
  setWallLoadBearing,
  setWallThickness as applyWallThickness,
  updateOpening,
  upsertRoomMeta,
} from '../model/graph';
import { computeMetrics, computeRooms, type Room } from '../model/rooms';
import { DEFAULT_WALL_THICKNESS, emptyDocument, emptyLevel } from '../model/types';
import { usePlanDocument } from '../state/usePlanDocument';
import { RevisionHistory } from './RevisionHistory';

interface EditorViewProps {
  project: Project;
  onBack: () => void;
  /** Lets the project list pick up a rename made here. */
  onProjectChanged: (project: Project) => void;
}

export function EditorView({
  project,
  onBack,
  onProjectChanged,
}: EditorViewProps): React.ReactElement {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [baseRevision, setBaseRevision] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [tool, setTool] = useState<Tool>('select');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [wallThickness, setWallThickness] = useState(DEFAULT_WALL_THICKNESS);
  const [fitSignal, setFitSignal] = useState(0);

  const [assets, setAssets] = useState<Asset[]>([]);
  const [backdropImage, setBackdropImage] = useState<HTMLImageElement | null>(null);
  const [showBackdrop, setShowBackdrop] = useState(true);
  // Bumped whenever the server-side history changes, to refresh the panel.
  const [historyToken, setHistoryToken] = useState(0);
  const [extracting, setExtracting] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [extraction, setExtraction] = useState<ExtractionStats | null>(null);

  const plan = usePlanDocument(emptyDocument());
  const { document: planDocument, level, editLevel, replaceDocument } = plan;

  const rooms = useMemo(() => computeRooms(level), [level]);

  // --- loading ---

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.listScenarios(project.id);
        if (cancelled) return;
        setScenarios(list);
        setScenarioId((current) => current ?? list[0]?.id ?? null);
        setAssets(await api.listAssets(project.id));
      } catch (cause) {
        if (!cancelled) setError(messageOf(cause, 'Could not load the project'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project.id]);

  const loadDocument = useCallback(
    async (id: string) => {
      setLoaded(false);
      try {
        const envelope = await api.getDocument(id);
        replaceDocument(envelope.document, { resetHistory: true });
        setBaseRevision(envelope.revision);
        setConflict(null);
        setError(null);
        setFitSignal((current) => current + 1);
      } catch (cause) {
        setError(messageOf(cause, 'Could not load the plan'));
      } finally {
        setLoaded(true);
      }
    },
    [replaceDocument],
  );

  useEffect(() => {
    if (scenarioId) void loadDocument(scenarioId);
  }, [scenarioId, loadDocument]);

  // --- backdrop ---

  const backdropAssetId = level.backdrop?.assetId ?? null;
  useEffect(() => {
    if (!backdropAssetId) {
      setBackdropImage(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        objectUrl = await api.assetObjectUrl(backdropAssetId);
        const image = new Image();
        image.src = objectUrl;
        await image.decode();
        if (!cancelled) setBackdropImage(image);
      } catch {
        if (!cancelled) setError('Could not load the plan image');
      }
    })();

    return () => {
      cancelled = true;
      // The object URL is ours to release; leaving it would leak the blob.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [backdropAssetId]);

  // --- saving ---

  const save = useCallback(async () => {
    if (!scenarioId || saving) return;

    setSaving(true);
    try {
      const metrics = computeMetrics(planDocument.levels);
      const revision = await api.commit(scenarioId, baseRevision, planDocument, metrics);
      setBaseRevision(revision.seq);
      plan.markSaved();
      setHistoryToken((current) => current + 1);
      setConflict(null);
      setError(null);
    } catch (cause) {
      if (cause instanceof RevisionConflictError) setConflict(cause.headRevision);
      else setError(messageOf(cause, 'Could not save'));
    } finally {
      setSaving(false);
    }
  }, [scenarioId, saving, planDocument, baseRevision, plan]);

  // Ctrl/Cmd+S saves, Ctrl/Cmd+Z undoes, and the tool keys switch tools.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void save();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) plan.redo();
        else plan.undo();
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const next = toolByKey.get(event.key.toLowerCase());
      if (next) setTool(next);

      if ((event.key === 'Delete' || event.key === 'Backspace') && selection) {
        deleteSelection();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save, plan, selection]);

  // Leaving with unsaved edits should not be silent.
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!plan.dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [plan.dirty]);

  // --- editing actions ---

  function deleteSelection(): void {
    if (!selection) return;
    if (selection.kind === 'wall') editLevel((current) => deleteWall(current, selection.id));
    else if (selection.kind === 'opening')
      editLevel((current) => deleteOpening(current, selection.id));
    else if (selection.kind === 'room')
      editLevel((current) => removeRoomMeta(current, selection.id));
    setSelection(null);
  }

  const uploadBackdrop = async (file: File) => {
    try {
      const asset = await api.uploadAsset(project.id, file);
      setAssets((current) => [...current, asset]);
      useAsBackdrop(asset.id);
    } catch (cause) {
      setError(messageOf(cause, 'Could not upload the image'));
    }
  };

  /**
   * Reads a plan out of an image and opens it as a new scenario.
   *
   * This is the path that matters: the point of the app is to get from a
   * picture to an editable model without drawing it by hand, so the editor is
   * for correcting what came out, not for tracing.
   */
  const extractFrom = async (asset: Asset) => {
    if (extracting) return;
    if (plan.dirty && !window.confirm('You have unsaved edits. Extract anyway?')) return;

    setExtracting(asset.id);
    setExtraction(null);
    setError(null);
    try {
      const result = await api.extractPlan(project.id, asset.id);
      setScenarios((current) => [...current, result.scenario]);
      setScenarioId(result.scenario.id);
      setExtraction(result.stats);
    } catch (cause) {
      setError(messageOf(cause, 'Could not read a plan from that image'));
    } finally {
      setExtracting(null);
    }
  };

  const removeAsset = async (asset: Asset) => {
    const inUse = level.backdrop?.assetId === asset.id;
    const ok = window.confirm(
      inUse
        ? `Delete "${asset.filename}"? It is the backdrop for this plan, which will be left with no image to trace over.`
        : `Delete "${asset.filename}"?`,
    );
    if (!ok) return;

    try {
      await api.deleteAsset(asset.id);
      setAssets((current) => current.filter((candidate) => candidate.id !== asset.id));
      if (inUse) useAsBackdrop('');
    } catch (cause) {
      setError(messageOf(cause, 'Could not delete the image'));
    }
  };

  // --- floors ---
  //
  // Every level carries its own backdrop, so a project with one image per
  // storey just needs a level per storey and the right image on each.

  const addFloor = () => {
    const name = window.prompt(
      'Name for the new floor',
      `Plan ${planDocument.levels.length + 1}`,
    );
    if (!name?.trim()) return;

    // Stack it on top of the highest existing floor so elevations stay sane
    // for the 3D view later.
    const top = planDocument.levels.reduce(
      (highest, candidate) => Math.max(highest, candidate.elevation + candidate.height),
      0,
    );
    const created = { ...emptyLevel(newId('lvl'), name.trim()), elevation: top };

    replaceDocument({ ...planDocument, levels: [...planDocument.levels, created] });
    plan.setLevelIndex(planDocument.levels.length);
    setSelection(null);
  };

  const renameFloor = () => {
    const name = window.prompt('Floor name', level.name);
    if (!name?.trim() || name.trim() === level.name) return;
    editLevel((current) => ({ ...current, name: name.trim() }));
  };

  const deleteFloor = () => {
    if (planDocument.levels.length < 2) return;
    const ok = window.confirm(`Delete the floor "${level.name}" and everything on it?`);
    if (!ok) return;

    const levels = planDocument.levels.filter((_, index) => index !== plan.levelIndex);
    replaceDocument({ ...planDocument, levels });
    plan.setLevelIndex(Math.max(0, plan.levelIndex - 1));
    setSelection(null);
  };

  const renameProject = async () => {
    const name = window.prompt('Project name', project.name);
    if (!name?.trim() || name.trim() === project.name) return;
    try {
      onProjectChanged(await api.updateProject(project.id, { name: name.trim() }));
    } catch (cause) {
      setError(messageOf(cause, 'Could not rename the project'));
    }
  };

  const renameScenario = async () => {
    if (!scenarioId) return;
    const current = scenarios.find((candidate) => candidate.id === scenarioId);
    const name = window.prompt('Scenario name', current?.name ?? '');
    if (!name?.trim() || name.trim() === current?.name) return;

    try {
      const updated = await api.updateScenario(scenarioId, { name: name.trim() });
      setScenarios((all) => all.map((s) => (s.id === updated.id ? updated : s)));
    } catch (cause) {
      setError(messageOf(cause, 'Could not rename the scenario'));
    }
  };

  const removeScenario = async () => {
    if (!scenarioId) return;
    const current = scenarios.find((candidate) => candidate.id === scenarioId);
    const ok = window.confirm(
      `Delete the scenario "${current?.name}" and its entire revision history? This cannot be undone.`,
    );
    if (!ok) return;

    try {
      await api.deleteScenario(scenarioId);
      const remaining = scenarios.filter((candidate) => candidate.id !== scenarioId);
      setScenarios(remaining);
      setScenarioId(remaining[0]?.id ?? null);
    } catch (cause) {
      // The API refuses to remove a project's only scenario, which is a
      // sensible answer rather than a failure -- show it as written.
      setError(messageOf(cause, 'Could not delete the scenario'));
    }
  };

  const useAsBackdrop = (assetId: string) => {
    editLevel((current) => ({
      ...current,
      // The empty option means "no backdrop", which has to clear it rather
      // than store an empty asset id the loader would then fail to fetch.
      backdrop: assetId
        ? {
            assetId,
            opacity: current.backdrop?.opacity ?? 0.6,
            // A neutral starting scale: calibration replaces it, and 10 mm/px
            // puts a typical listing image at roughly house size to begin with.
            transform: current.backdrop?.transform ?? {
              x: 0,
              y: 0,
              rotation: 0,
              mmPerPixel: 10,
            },
          }
        : null,
    }));
    setFitSignal((current) => current + 1);
  };

  const createScenario = async (forkFrom?: string) => {
    const name = window.prompt(
      forkFrom ? 'Name for the new variant' : 'Name for the new scenario',
      forkFrom ? 'Open kitchen' : 'Variant',
    );
    if (!name?.trim()) return;

    try {
      const scenario = await api.createScenario(project.id, name.trim(), forkFrom);
      setScenarios((current) => [...current, scenario]);
      setScenarioId(scenario.id);
    } catch (cause) {
      setError(messageOf(cause, 'Could not create the scenario'));
    }
  };

  const scenario = scenarios.find((candidate) => candidate.id === scenarioId) ?? null;
  const selectedWall =
    selection?.kind === 'wall' ? level.walls.find((w) => w.id === selection.id) : undefined;
  const selectedOpening =
    selection?.kind === 'opening' ? level.openings.find((o) => o.id === selection.id) : undefined;
  const selectedRoom =
    selection?.kind === 'room' ? rooms.find((room) => room.id === selection.id) : undefined;

  return (
    <div className="editor">
      <header className="toolbar">
        <button className="ghost" onClick={onBack}>
          ← Projects
        </button>
        <strong className="project-title">{project.name}</strong>

        <select
          value={scenarioId ?? ''}
          onChange={(event) => setScenarioId(event.target.value)}
          aria-label="Scenario"
        >
          {scenarios.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </select>
        <button className="ghost" onClick={() => void createScenario(scenarioId ?? undefined)}>
          Fork
        </button>

        <span className="toolbar-gap" />

        <div className="tool-group" role="group" aria-label="Tools">
          {TOOLS.map((definition) => (
            <button
              key={definition.id}
              className={tool === definition.id ? 'active' : ''}
              onClick={() => setTool(definition.id)}
              title={`${definition.hint} (${definition.key.toUpperCase()})`}
            >
              {definition.label}
            </button>
          ))}
        </div>

        <span className="toolbar-gap" />

        <button className="ghost" onClick={plan.undo} disabled={!plan.canUndo} title="Ctrl+Z">
          Undo
        </button>
        <button className="ghost" onClick={plan.redo} disabled={!plan.canRedo} title="Ctrl+Shift+Z">
          Redo
        </button>
        <button className="ghost" onClick={() => setFitSignal((current) => current + 1)}>
          Fit
        </button>
        <button onClick={() => void save()} disabled={!plan.dirty || saving}>
          {saving ? 'Saving…' : plan.dirty ? 'Save' : `Saved · r${baseRevision}`}
        </button>
      </header>

      {conflict !== null && (
        <div className="banner warning">
          This scenario moved on to revision {conflict} while you were editing, so your save was
          refused rather than overwriting it.{' '}
          <button className="ghost" onClick={() => scenarioId && void loadDocument(scenarioId)}>
            Reload revision {conflict}
          </button>{' '}
          — your current edits will be lost.
        </div>
      )}
      {error && <div className="banner error">{error}</div>}

      <div className="editor-body">
        <div className="canvas-area">
          {loaded ? (
            <PlanCanvas
              level={level}
              rooms={rooms}
              tool={tool}
              wallThickness={wallThickness}
              selection={selection}
              backdrop={backdropImage}
              showBackdrop={showBackdrop}
              fitSignal={fitSignal}
              onSelect={setSelection}
              onEdit={editLevel}
            />
          ) : (
            <div className="canvas-host muted centred">Loading plan…</div>
          )}

          {loaded && level.walls.length === 0 && (
            <div className="start-here">
              {!level.backdrop ? (
                assets.length === 0 ? (
                  <>
                    <h2>Start with the floor plan image</h2>
                    <p>
                      Upload the plan and the walls, doors and windows are read out of it for
                      you. Drawing by hand is the fallback, not the point.
                    </p>
                    <button className="primary" onClick={() => fileInputRef.current?.click()}>
                      Upload a plan image
                    </button>
                  </>
                ) : (
                  <>
                    <h2>Pick the image for {level.name}</h2>
                    <div className="preset-row">
                      {assets.map((asset) => (
                        <button key={asset.id} onClick={() => useAsBackdrop(asset.id)}>
                          {asset.filename}
                        </button>
                      ))}
                    </div>
                  </>
                )
              ) : (
                <>
                  <h2>Read the plan out of this image</h2>
                  <p>
                    Finds the walls, doors and windows and turns them into a model you can edit.
                    Room names are not read, so you will name those yourself.
                  </p>
                  <button
                    className="primary"
                    disabled={extracting !== null}
                    onClick={() => {
                      const asset = assets.find((a) => a.id === level.backdrop?.assetId);
                      if (asset) void extractFrom(asset);
                    }}
                  >
                    {extracting ? 'Reading the plan…' : 'Extract walls, doors and windows'}
                  </button>
                  <p className="muted small">Or draw over it by hand with the Wall tool.</p>
                </>
              )}
            </div>
          )}
        </div>

        <aside className="sidebar">
          <p className="hint">{TOOLS.find((t) => t.id === tool)?.hint}</p>

          {extraction && (
            <section>
              <h2>Extracted</h2>
              <p className="muted small">
                {extraction.walls} walls, {extraction.junctions} junctions, {extraction.doors}{' '}
                doors, {extraction.windows} windows, {extraction.openings} openings.
              </p>
              <p className="muted small">Scale: {extraction.scaleBasis}.</p>
              <p className="muted small">
                Rooms are unnamed — the extractor reads geometry, not text. Check it against the
                image behind it and correct anything it got wrong.
              </p>
              <button className="ghost" onClick={() => setExtraction(null)}>
                Dismiss
              </button>
            </section>
          )}

          <section>
            <h2>Floors</h2>
            <p className="muted small">
              Each floor keeps its own plan image, walls and rooms. Add one per storey and give
              each the matching image.
            </p>
            <ul className="floor-list">
              {planDocument.levels.map((candidate, index) => (
                <li key={candidate.id}>
                  <button
                    className={`floor-pick ${index === plan.levelIndex ? 'active' : ''}`}
                    onClick={() => {
                      plan.setLevelIndex(index);
                      setSelection(null);
                      setFitSignal((current) => current + 1);
                    }}
                  >
                    {candidate.name}
                    <span className="muted small">
                      {candidate.backdrop ? ' · image' : ' · no image'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="button-row">
              <button className="ghost" onClick={addFloor}>
                Add floor
              </button>
              <button className="ghost" onClick={renameFloor}>
                Rename
              </button>
              <button
                className="danger ghost"
                disabled={planDocument.levels.length < 2}
                title={
                  planDocument.levels.length < 2
                    ? 'A plan needs at least one floor'
                    : 'Delete this floor'
                }
                onClick={deleteFloor}
              >
                Delete
              </button>
            </div>
          </section>

          <section>
            <h2>Plan image for {level.name}</h2>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void uploadBackdrop(file);
                event.target.value = '';
              }}
            />
            {assets.length > 0 && (
              <ul className="asset-list">
                {assets.map((asset) => (
                  <li key={asset.id} className={backdropAssetId === asset.id ? 'current' : ''}>
                    <button
                      className={`asset-pick ${backdropAssetId === asset.id ? 'active' : ''}`}
                      onClick={() => useAsBackdrop(backdropAssetId === asset.id ? '' : asset.id)}
                      title={backdropAssetId === asset.id ? 'Stop using this image' : 'Trace over this image'}
                    >
                      {asset.filename}
                      <span className="muted small"> {Math.round(asset.size / 1024)} kB</span>
                    </button>
                    <button
                      className="small-button"
                      disabled={extracting !== null}
                      title="Read the walls, doors and windows out of this image"
                      onClick={() => void extractFrom(asset)}
                    >
                      {extracting === asset.id ? 'Reading…' : 'Extract'}
                    </button>
                    <button
                      className="danger ghost small-button"
                      onClick={() => void removeAsset(asset)}
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {level.backdrop && (
              <>
                <label className="row">
                  <input
                    type="checkbox"
                    checked={showBackdrop}
                    onChange={(event) => setShowBackdrop(event.target.checked)}
                  />
                  Show image
                </label>
                <label className="row">
                  Opacity
                  <input
                    type="range"
                    min={0.1}
                    max={1}
                    step={0.05}
                    value={level.backdrop.opacity}
                    onChange={(event) =>
                      editLevel((current) =>
                        current.backdrop
                          ? {
                              ...current,
                              backdrop: { ...current.backdrop, opacity: Number(event.target.value) },
                            }
                          : current,
                      )
                    }
                  />
                </label>
                <label className="row">
                  Image scale (mm/px)
                  <input
                    type="number"
                    min={0.1}
                    step={0.1}
                    value={level.backdrop.transform.mmPerPixel}
                    onChange={(event) =>
                      editLevel((current) =>
                        current.backdrop
                          ? {
                              ...current,
                              backdrop: {
                                ...current.backdrop,
                                transform: {
                                  ...current.backdrop.transform,
                                  mmPerPixel: Number(event.target.value) || 1,
                                },
                              },
                            }
                          : current,
                      )
                    }
                  />
                </label>
              </>
            )}
          </section>

          <section>
            <h2>New walls</h2>
            <label className="row">
              Thickness
              <input
                type="number"
                min={10}
                step={10}
                value={wallThickness}
                onChange={(event) => setWallThickness(Number(event.target.value) || 100)}
              />
              mm
            </label>
          </section>

          {selectedWall && (
            <section>
              <h2>Wall</h2>
              <label className="row">
                Thickness
                <input
                  type="number"
                  min={10}
                  step={10}
                  value={selectedWall.thickness}
                  onChange={(event) =>
                    editLevel((current) =>
                      applyWallThickness(current, selectedWall.id, Number(event.target.value)),
                    )
                  }
                />
                mm
              </label>
              <label className="row">
                <input
                  type="checkbox"
                  checked={selectedWall.loadBearing ?? false}
                  onChange={(event) =>
                    editLevel((current) =>
                      setWallLoadBearing(current, selectedWall.id, event.target.checked),
                    )
                  }
                />
                Load-bearing
              </label>
              <p className="muted small">
                Nothing infers this from the drawing. Flagging it is what makes a removal show up
                as one that needs an engineer.
              </p>
              <button className="danger" onClick={deleteSelection}>
                Delete wall
              </button>
            </section>
          )}

          {selectedOpening && (
            <section>
              <h2>{selectedOpening.type}</h2>
              <label className="row">
                Width
                <input
                  type="number"
                  min={100}
                  step={50}
                  value={selectedOpening.width}
                  onChange={(event) =>
                    editLevel((current) =>
                      updateOpening(current, selectedOpening.id, {
                        width: Number(event.target.value) || 100,
                      }),
                    )
                  }
                />
                mm
              </label>
              <label className="row">
                Sill height
                <input
                  type="number"
                  min={0}
                  step={50}
                  value={selectedOpening.sill}
                  onChange={(event) =>
                    editLevel((current) =>
                      updateOpening(current, selectedOpening.id, {
                        sill: Number(event.target.value) || 0,
                      }),
                    )
                  }
                />
                mm
              </label>
              <button className="danger" onClick={deleteSelection}>
                Delete
              </button>
            </section>
          )}

          {selectedRoom && (
            <RoomEditor
              room={selectedRoom}
              onChange={(name, declaredAreaM2) =>
                editLevel((current) =>
                  upsertRoomMeta(current, {
                    id: selectedRoom.meta?.id ?? newId('r'),
                    seed: selectedRoom.meta?.seed ?? selectedRoom.seed,
                    name,
                    declaredAreaM2,
                  }),
                )
              }
            />
          )}

          <section>
            <h2>Rooms</h2>
            {rooms.length === 0 ? (
              <p className="muted small">
                No enclosed rooms yet. Rooms appear by themselves once the walls form a closed
                loop — they are never drawn directly.
              </p>
            ) : (
              <table className="rooms">
                <tbody>
                  {rooms.map((room) => {
                    const declared = room.meta?.declaredAreaM2;
                    const off = declared ? Math.abs(room.areaM2 - declared) / declared > 0.05 : false;
                    return (
                      <tr
                        key={room.id}
                        className={selection?.kind === 'room' && selection.id === room.id ? 'selected' : ''}
                        onClick={() => setSelection({ kind: 'room', id: room.id })}
                      >
                        <td>{room.meta?.name ?? <span className="muted">Unnamed</span>}</td>
                        <td className="numeric">{room.areaM2.toFixed(1)} m²</td>
                        <td className={`numeric ${off ? 'warn' : 'muted'}`}>
                          {declared ? `${declared.toFixed(1)}` : ''}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="total">
                    <td>Total</td>
                    <td className="numeric">
                      {rooms.reduce((sum, room) => sum + room.areaM2, 0).toFixed(1)} m²
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            )}
          </section>

          {scenario && (
            <section>
              <h2>Scenario</h2>
              <p className="muted small">
                {scenario.name} · revision {baseRevision}
                {scenario.forkedFrom && ' · forked'}
              </p>
              <div className="button-row">
                <button className="ghost" onClick={() => void renameScenario()}>
                  Rename
                </button>
                <button className="ghost" onClick={() => void createScenario()}>
                  New scenario
                </button>
                <button
                  className="danger ghost"
                  disabled={scenarios.length < 2}
                  title={
                    scenarios.length < 2
                      ? 'A project has to keep at least one scenario'
                      : 'Delete this scenario and its history'
                  }
                  onClick={() => void removeScenario()}
                >
                  Delete
                </button>
              </div>
            </section>
          )}

          {scenarioId && (
            <RevisionHistory
              scenarioId={scenarioId}
              refreshToken={historyToken}
              currentRevision={baseRevision}
              dirty={plan.dirty}
              onRestored={() => {
                setHistoryToken((current) => current + 1);
                void loadDocument(scenarioId);
              }}
              onError={setError}
            />
          )}

          <section>
            <h2>Project</h2>
            <p className="muted small">{project.name}</p>
            <button className="ghost" onClick={() => void renameProject()}>
              Rename project
            </button>
          </section>
        </aside>
      </div>
    </div>
  );
}

function RoomEditor({
  room,
  onChange,
}: {
  room: Room;
  onChange: (name: string, declaredAreaM2: number | undefined) => void;
}): React.ReactElement {
  const [name, setName] = useState(room.meta?.name ?? '');
  const [declared, setDeclared] = useState(room.meta?.declaredAreaM2?.toString() ?? '');

  useEffect(() => {
    setName(room.meta?.name ?? '');
    setDeclared(room.meta?.declaredAreaM2?.toString() ?? '');
  }, [room.id, room.meta?.name, room.meta?.declaredAreaM2]);

  const commit = () => {
    const parsed = Number(declared.replace(',', '.'));
    onChange(name.trim(), Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
  };

  return (
    <section>
      <h2>Room</h2>
      <label className="row">
        Name
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => event.key === 'Enter' && commit()}
          placeholder="Kök"
        />
      </label>
      <label className="row">
        Printed area
        <input
          value={declared}
          onChange={(event) => setDeclared(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => event.key === 'Enter' && commit()}
          placeholder="12,5"
        />
        m²
      </label>
      <p className="muted small">
        Measured: {room.areaM2.toFixed(2)} m². Entering the area printed on the plan gives the
        editor something to check the tracing against.
      </p>
    </section>
  );
}

function messageOf(cause: unknown, fallback: string): string {
  return cause instanceof Error ? cause.message : fallback;
}
